import { useCallback, useRef, useState } from 'react';
import { apiService, CHECKOUT_API_CONFIG } from '../../services/apiService';
import {
  asCoordinator,
  type IdentifierValue,
  type KycProfile,
  type MissingIdentifiers,
  type OnrampCoordinator,
} from './thixOnrampCoordinator';

/**
 * First slice of the inline 3thix onramp: start the flow on our backend, load Stripe's Crypto
 * Onramp SDK, and initialise its coordinator — all on our own checkout page, with no redirect to
 * pay.3thix.com.
 *
 * Deliberately stops there. Everything after coordinator init (Link sign-in/registration, KYC,
 * card entry, session + quote + checkout) depends on the coordinator actually existing and on the
 * shape of what it hands back, and none of that can be verified from a server. This slice exists
 * to answer one question against a real browser — does the SDK boot on our origin — before more is
 * built on the assumption that it does.
 */

/** What our own backend returns from POST /checkout/orders/{token}/onramp/start. */
export interface OnrampStart {
  externalOrderId: string;
  email: string;
  amountCents: number;
  currency: string;
  depositAddress: string;
  network: string;
  alreadyPaid: boolean;
  /** true when 3thix has no Link account for this email yet — routes to sign-up, not sign-in.
   *  It comes from 3thix answering 404 on auth-intent, which is a routing signal, not an error. */
  needsRegistration: boolean;
  authIntentId?: string;
  /** Stripe publishable key from 3thix's /public/config — the SDK's FIRST POSITIONAL argument. */
  stripePublishableKey: string;
  /** Each entry has a kycRegion of us/uk/eu, deciding SSN vs EU identifier at verification. */
  onrampCountries?: Array<{ code: string; name: string; dialCode: string; kycRegion: string }>;
  asset?: string;
  /** Short-lived signed token binding merchant + order + auth intent; 3thix wants it back on
   *  review-pending, so it has to be carried through the flow. */
  reviewBinding?: string;
}

export type OnrampPhase =
  | 'idle'
  | 'starting'      // calling our backend
  | 'loading-sdk'   // fetching crypto-onramp.js
  | 'initialising'  // loadCryptoOnrampAndInitialize
  | 'ready'         // coordinator exists and is usable
  | 'authenticating'  // Link sign-in — Stripe may mount its own OTP field
  | 'registering'     // Link sign-up for a shopper with no Link account
  | 'authenticated'   // Link identity established
  | 'collecting-profile' // name / address / dob / SSN — required before anything else
  | 'collecting-kyc'  // shopper is filling in whatever the server asked for
  | 'submitting-kyc'
  | 'kyc-complete'    // no identifiers outstanding — payment details are next
  | 'verifying-document' // provider asked for photo ID + selfie (step-up tier l2)
  | 'review-pending'     // documents submitted, provider still reviewing
  | 'collecting-card' // Stripe's card form is mounted; shopper is filling it in
  | 'card-added'      // payment token captured; ready to charge
  | 'paying'          // performCheckout is driving 3DS + settlement
  | 'paid'
  | 'error';

const SDK_SRC = 'https://js.stripe.com/crypto-onramp/v1/crypto-onramp.js';

declare global {
  interface Window {
    loadCryptoOnrampAndInitialize?: (...args: unknown[]) => Promise<unknown>;
  }
}

/**
 * Loads the Crypto Onramp SDK once per page. It is an ES module, so it must be injected with
 * type="module" — a plain script tag silently fails to define the global.
 */
function loadSdk(): Promise<NonNullable<Window['loadCryptoOnrampAndInitialize']>> {
  if (window.loadCryptoOnrampAndInitialize) {
    return Promise.resolve(window.loadCryptoOnrampAndInitialize);
  }
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${SDK_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () =>
        window.loadCryptoOnrampAndInitialize
          ? resolve(window.loadCryptoOnrampAndInitialize)
          : reject(new Error('SDK loaded but did not define loadCryptoOnrampAndInitialize')));
      existing.addEventListener('error', () => reject(new Error('Could not load the payment SDK')));
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = SDK_SRC;
    script.onload = () =>
      window.loadCryptoOnrampAndInitialize
        ? resolve(window.loadCryptoOnrampAndInitialize)
        : reject(new Error('SDK loaded but did not define loadCryptoOnrampAndInitialize'));
    script.onerror = () => reject(new Error('Could not load the payment SDK'));
    document.head.appendChild(script);
  });
}


/**
 * Normalises a shopper-typed phone number to E.164 (+<country><national>), which is what Stripe
 * requires — a bare "18703450042" is rejected with a parsing error. 3thix's own client does the
 * same thing, which is why /public/config ships a dialCode per country.
 *
 * Handles the three ways people actually type it: with a +, with the country code but no +, and
 * national-only. The middle case is why this is not just a string concat — prepending the dial
 * code to a number that already starts with it produces "+1 1870…", which is equally invalid.
 */
export function toE164(input: string, dialCode?: string): string {
  const trimmed = (input || '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('+')) return '+' + trimmed.slice(1).replace(/\D/g, '');

  const digits = trimmed.replace(/\D/g, '');
  const dial = (dialCode || '').replace(/\D/g, '');
  if (!dial) return '+' + digits;

  // Already carries the country code: keep it, but only when what remains is still a plausible
  // national number — otherwise a national number that merely happens to begin with the dial
  // digits (a US 1-800…, say) would be silently truncated.
  if (digits.startsWith(dial) && digits.length - dial.length >= 7) {
    return '+' + digits;
  }
  return '+' + dial + digits;
}

export function useThixOnramp(token: string | undefined) {
  const [phase, setPhase] = useState<OnrampPhase>('idle');
  const [error, setError] = useState<string>('');
  const [start, setStart] = useState<OnrampStart | null>(null);
  const [missing, setMissing] = useState<MissingIdentifiers | null>(null);
  /** Set by authenticate()'s callback. Everything downstream (customer-state, sessions) keys on
   *  it, and it exists only once the shopper has actually completed the OTP. */
  const [cryptoCustomerId, setCryptoCustomerId] = useState<string | null>(null);
  /** Where Stripe's OTP iframe gets appended — authenticate() hands back a DETACHED div. */
  const otpHostRef = useRef<HTMLDivElement | null>(null);
  /** Stripe's card form is appended here — collectPaymentMethod also returns a detached element. */
  const cardHostRef = useRef<HTMLDivElement | null>(null);
  /**
   * The residence the KYC profile declared. Kept because session creation needs it again:
   * /onramp/headless/sessions takes `country` (required) and `state`, and they must be the same
   * residence KYC was performed against — 3thix re-checks eligibility (unsupported_country,
   * restricted_us_state) at session time, not just at KYC time.
   */
  /**
   * The auth intent the Stripe SDK actually authenticated, and its binding.
   *
   * Held separately from `start` on purpose. /start calls /auth-intent, which mints a NEW intent
   * every time, and only the intent that was signed in through the SDK is usable: customer-state
   * and sessions answer 502 for an unauthenticated one (confirmed live 2026-09-11 — a fresh intent
   * 502s and the authenticated one 200s, same customer, same second). So once authentication
   * succeeds, this pair is the only one anything downstream may use.
   */
  const [authedIntent, setAuthedIntent] = useState<{ id: string; binding?: string } | null>(null);
  const [kycCountry, setKycCountry] = useState<string | null>(null);
  const [kycState, setKycState] = useState<string | null>(null);
  const [paymentToken, setPaymentToken] = useState<string | null>(null);
  const [onrampSessionId, setOnrampSessionId] = useState<string | null>(null);
  /** Kept in a ref, not state: it is a live SDK handle, not render input, and putting a non-frozen
   *  third-party object in state invites React to treat it as comparable. */
  const coordinatorRef = useRef<OnrampCoordinator | null>(null);

  /**
   * Reports progress to 3thix through our backend. Fire-and-forget by contract: a telemetry
   * failure must never surface to the shopper or block the payment.
   */
  const reportStep = useCallback(
    (step?: string, failureCategory?: string) => {
      if (!token) return;
      void apiService
        .post(`/checkout/orders/${token}/onramp/step`, { step, failureCategory }, CHECKOUT_API_CONFIG)
        .catch(() => undefined);
    },
    [token],
  );

  const begin = useCallback(
    async (email?: string) => {
      if (!token) return;
      setError('');
      setPhase('starting');
      try {
        const res: any = await apiService.post(`/checkout/orders/${token}/onramp/start`, { email }, CHECKOUT_API_CONFIG);
        const data: OnrampStart = res?.data ?? res;
        setStart(data);

        setPhase('loading-sdk');
        const initialize = await loadSdk();

        setPhase('initialising');
        // The publishable key is the FIRST POSITIONAL argument, not a field on the options object.
        // Passing options alone still constructs a coordinator and even sets ready=true, but Stripe
        // rejects its own controller iframe with 403 Forbidden and nothing works — a silent failure
        // confirmed live on 2026-09-10. The key comes from 3thix's /public/config via our backend.
        if (!data.stripePublishableKey) {
          throw new Error('Payment provider did not return a publishable key.');
        }
        const coordinator = asCoordinator(
          await initialize(data.stripePublishableKey, { appearance: { theme: 'stripe' } }),
        );
        coordinatorRef.current = coordinator;

        // eslint-disable-next-line no-console
        // Own keys AND prototype methods: a class instance keeps its API on the prototype, so
        // Object.keys() alone shows only state (ready, sessionId, controllerFrame…) and none of
        // the methods the next slice needs to call.
        console.info('[onramp] coordinator initialised', {
          own: coordinator && typeof coordinator === 'object'
            ? Object.keys(coordinator as object) : null,
          proto: coordinator && typeof coordinator === 'object'
            ? Object.getOwnPropertyNames(Object.getPrototypeOf(coordinator)) : null,
        });

        reportStep(data.needsRegistration ? 'link_register' : 'link_signin');
        setPhase('ready');
      } catch (err: any) {
        const message =
          err?.response?.data?.message || err?.message || 'Could not start the payment.';
        setError(message);
        setPhase('error');
        reportStep(undefined, 'link_registration_error');
        // eslint-disable-next-line no-console
        console.error('[onramp] failed', err);
      }
    },
    [token, reportStep],
  );

  /**
   * Slice 2 — establish the shopper's Link identity, on our page.
   *
   * Two paths, decided by whether 3thix already knows this email: `authenticate` for an existing
   * Link account (Stripe mounts its own one-time-code field into `otpSelector` when it wants a
   * code, which is why this can sit unresolved until the shopper types it), or `registerLinkUser`
   * to create one.
   *
   * Registration is positional — (email, phoneNumber, country, legalName) — per Stripe's own SDK
   * source. `country` must be an ISO alpha-2 that 3thix supports; the caller picks it from the
   * onrampCountries list /start returns, which also carries the kycRegion driving the next slice.
   */
  /**
   * Slice 2 (corrected) — establish the shopper's Link identity on our page.
   *
   * Registration and sign-in converge: registering only STARTS a signup verification session
   * (the sign_up response shows state:"STARTED"), so both paths still have to complete the
   * one-time code through authenticate(). After registering we re-run /start, because the auth
   * intent that 3thix 404'd on before now exists.
   *
   * Completion is signalled ONLY by authenticate's callback — its promise resolves as soon as the
   * OTP iframe mounts. This resolves when the shopper is genuinely authenticated, or rejects.
   */
  const authenticateLink = useCallback(
    async (args: { phoneNumber?: string; country?: string; legalName?: string }) => {
      const coordinator = coordinatorRef.current;
      if (!coordinator || !start) {
        setError('Start the payment before verifying your identity.');
        return false;
      }
      setError('');
      try {
        let authIntentId = start.authIntentId;
        // Tracked alongside authIntentId rather than read from `start` later: after registration
        // both are replaced by the refreshed /start below, and `start` in this closure is still
        // the pre-registration value (which by definition has neither). Reading the binding off
        // the stale copy sent customer-state with no binding at all -> 403 invalid_review_binding.
        let reviewBinding = start.reviewBinding;

        if (start.needsRegistration) {
          if (!args.phoneNumber || !args.country || !args.legalName) {
            setError('Please provide your name, phone number and country.');
            return false;
          }
          const dialCode = start.onrampCountries
            ?.find((c) => c.code === args.country)?.dialCode;
          const phone = toE164(args.phoneNumber, dialCode);
          if (!/^\+\d{8,15}$/.test(phone)) {
            setError('Please enter a valid phone number for the country you selected.');
            return false;
          }
          setPhase('registering');
          await coordinator.registerLinkUser(
            start.email, phone, args.country, args.legalName);
          reportStep('link_register');

          // The Link account exists now, so the auth intent that 404'd before can be minted.
          const res: any = await apiService.post(
            `/checkout/orders/${token}/onramp/start`, { email: start.email }, CHECKOUT_API_CONFIG);
          const refreshed: OnrampStart = res?.data ?? res;
          setStart(refreshed);
          authIntentId = refreshed.authIntentId;
          reviewBinding = refreshed.reviewBinding;
        }

        if (!authIntentId) {
          setError('Verification could not be started. Please try again.');
          setPhase('error');
          return false;
        }

        setPhase('authenticating');
        const outcome = await new Promise<{ crypto_customer_id?: string; result: string }>(
          (resolve, reject) => {
            coordinator
              .authenticate(authIntentId as string, resolve)
              .then((element) => {
                // Detached div holding Stripe's OTP iframe — it renders nothing until appended.
                if (element && otpHostRef.current) {
                  otpHostRef.current.replaceChildren(element);
                }
              })
              .catch(reject);
          });

        if (outcome.result !== 'success') {
          setError(outcome.result === 'declined'
            ? 'Verification was declined.'
            : 'Verification was not completed.');
          setPhase('error');
          reportStep(undefined, outcome.result === 'declined'
            ? 'link_signin_error' : 'link_signin_incomplete');
          return false;
        }

        // Unmount Stripe's OTP iframe now that it has done its job. Their own client clears this
        // host before moving on; leaving it mounted meant "Confirm it's you" stayed on screen
        // underneath the identity form, so the shopper saw two live steps at once.
        if (otpHostRef.current) otpHostRef.current.replaceChildren();

        const newCryptoCustomerId = outcome.crypto_customer_id ?? null;
        setCryptoCustomerId(newCryptoCustomerId);
        setAuthedIntent({ id: authIntentId as string, binding: reviewBinding });
        reportStep('link_verified');

        // Read the customer's state as soon as the identity exists — this is where 3thix's own
        // client reads it (hlRouteNext), before KYC, to decide where to send the shopper. Doing it
        // here rather than only at card collection also means the server logs what the provider
        // thinks of a customer at the earliest possible moment, which is the difference between
        // diagnosing a stuck account in one step and inferring it from a failure three steps later.
        // Best-effort: routing still runs off the existing phases, so a failure must not block.
        if (newCryptoCustomerId) {
          void apiService
            .post(`/checkout/orders/${token}/onramp/customer-state`, {
              cryptoCustomerId: newCryptoCustomerId,
              authIntentId,
              reviewBinding,
            }, CHECKOUT_API_CONFIG)
            .catch(() => undefined);
        }

        setPhase('authenticated');
        return true;
      } catch (err: any) {
        setError(err?.message || 'Identity verification failed.');
        setPhase('error');
        reportStep(undefined, start.needsRegistration
          ? 'link_registration_error' : 'link_signin_error');
        // eslint-disable-next-line no-console
        console.error('[onramp] link auth failed', err);
        return false;
      }
    },
    [start, token, reportStep],
  );

  /**
   * Slice 3a — the base identity profile.
   *
   * This has to happen before getMissingIdentifiers, which otherwise refuses with
   * "Basic KYC information must be submitted before this endpoint can be used". Stripe wants the
   * profile first, then tells you what else it needs.
   *
   * Two regional rules, both taken from 3thix's own client rather than guessed:
   *  - US: the FULL 9-digit SSN. Last-4 is silently rejected, so a shopper who enters 4 digits
   *    sees no error and simply never progresses.
   *  - EU: nationalities, birth_city and birth_country are additionally required.
   */
  /** Moves from 'authenticated' into the profile form. Separate from submitProfile so the UI can
   *  render the form before anything is sent. */
  const beginProfile = useCallback(() => {
    setError('');
    setPhase('collecting-profile');
  }, []);

  const submitProfile = useCallback(
    async (profile: KycProfile) => {
      const coordinator = coordinatorRef.current;
      if (!coordinator) return false;
      setError('');
      setPhase('submitting-kyc');
      // Recorded before the call so the "already verified" short-circuit below still captures it.
      setKycCountry(profile.address.country || null);
      setKycState(profile.address.state || null);
      try {
        await coordinator.submitKycInfo(profile);
        reportStep('kyc');
        // Stripe can accept the profile without returning a verdict while it propagates, so the
        // next call is what actually decides where the shopper goes.
        return true;
      } catch (err: any) {
        const message: string = err?.message || '';
        // Not a failure: this Link consumer already passed KYC on an earlier attempt, and Stripe
        // refuses to re-submit a profile for a verified person. Their own client avoids hitting
        // this by routing on /customer-state first; treating it as success is equivalent for our
        // purposes and keeps a returning shopper from being blocked by their own history.
        if (/already been verified/i.test(message)) {
          // eslint-disable-next-line no-console
          console.info('[onramp] consumer already verified — skipping profile submission');
          reportStep('kyc');
          return true;
        }
        setError(message || 'Could not save your details.');
        setPhase('error');
        reportStep(undefined, 'kyc_submission_error');
        // eslint-disable-next-line no-console
        console.error('[onramp] submitKycInfo failed', err);
        return false;
      }
    },
    [reportStep],
  );

  /**
   * Slice 3a — ask Stripe what identity data is still outstanding.
   *
   * The set of identifier types is decided server-side per region (kycRegion us/uk/eu on the
   * country from /public/config), so nothing here hardcodes SSN or EU identifiers: whatever comes
   * back is what the UI renders. Also registers the deposit wallet 3thix handed us at /prepare,
   * which their own flow does at this point.
   */
  const loadKycRequirements = useCallback(async (countryCode?: string) => {
    const coordinator = coordinatorRef.current;
    if (!coordinator || !start) return null;
    setError('');
    try {
      if (start.depositAddress && start.network) {
        // Idempotent in practice — re-registering the same wallet is how their client behaves on
        // a resumed session — but never let it sink the step.
        try {
          await coordinator.registerWalletAddress(start.depositAddress, start.network);
        } catch (walletErr) {
          // eslint-disable-next-line no-console
          console.warn('[onramp] wallet registration skipped', walletErr);
        }
      }
      // EU ONLY. getMissingIdentifiers backs the DAC8/CARF national-identifier step — its
      // vocabulary is things like pl_pesel and mt_pp — and Stripe refuses it for a non-EU user
      // with "Request made on a user who doesn't have an EU-based address" (confirmed live
      // 2026-09-10). 3thix's own client only calls it from hlEuCollectIdentifiers.
      //
      // For the US the SSN went in with submitKycInfo, so KYC is already complete here.
      const region = start.onrampCountries
        ?.find((c) => c.code === (countryCode || ''))?.kycRegion;
      if (region !== 'eu') {
        setMissing(null);
        setPhase('kyc-complete');
        reportStep('kyc');
        return null;
      }

      const requirements = await coordinator.getMissingIdentifiers();
      setMissing(requirements);
      setPhase(requirements.identifiers.length === 0 ? 'kyc-complete' : 'collecting-kyc');
      return requirements;
    } catch (err: any) {
      setError(err?.message || 'Could not load verification requirements.');
      setPhase('error');
      // eslint-disable-next-line no-console
      console.error('[onramp] getMissingIdentifiers failed', err);
      return null;
    }
  }, [start, reportStep]);

  /**
   * Slice 3b — submit identifier values.
   *
   * This is a loop, not a single call: updateKycInfo answers with what is STILL missing, so the
   * server can ask for more after seeing the first answers. Phase only reaches kyc-complete when
   * it reports completed.
   */
  const submitIdentifiers = useCallback(
    async (values: IdentifierValue[]) => {
      const coordinator = coordinatorRef.current;
      if (!coordinator) return false;
      setError('');
      setPhase('submitting-kyc');
      try {
        const result = await coordinator.updateKycInfo(values);
        setMissing({
          identifiers: result.identifiers,
          alternatives: result.alternatives,
        });
        if (result.completed) {
          setPhase('kyc-complete');
          reportStep('kyc');
          return true;
        }
        // Still outstanding — the server asked for more after seeing these.
        setPhase('collecting-kyc');
        return false;
      } catch (err: any) {
        setError(err?.message || 'Verification could not be completed.');
        setPhase('error');
        reportStep(undefined, 'kyc_submission_error');
        // eslint-disable-next-line no-console
        console.error('[onramp] updateKycInfo failed', err);
        return false;
      }
    },
    [reportStep],
  );

  /**
   * Slice 4a — collect card details.
   *
   * Stripe renders the form inside its own iframe, so the card number never enters our DOM. That
   * is what keeps this out of PCI scope even though the page is ours. Like authenticate(), the
   * promise resolving means the form MOUNTED — the token arrives via the callback.
   */
  const collectCard = useCallback(async () => {
    const coordinator = coordinatorRef.current;
    if (!coordinator) return false;
    setError('');
    setPhase('collecting-card');
    try {
      // A card already saved to the shopper's Link account is spent directly, without mounting
      // Stripe's payment element at all.
      //
      // This is not a shortcut — it is the only reliable path for a returning shopper. Stripe's
      // checkout leg accepts ONLY cards saved to the Link account, and a card typed fresh into the
      // element is not persisted there, so sending a returning shopper through the element gets
      // them a "confirm it's you" re-authentication panel for the card they already have and can
      // hang there. 3thix's own client documents this as a Stripe preview gap and routes around it
      // exactly this way.
      //
      // Best-effort: if the lookup fails for any reason we fall through to the element rather than
      // block the payment on it.
      if (cryptoCustomerId) {
        try {
          const res: any = await apiService.post(
            `/checkout/orders/${token}/onramp/customer-state`, {
              cryptoCustomerId,
              authIntentId: start?.authIntentId,
              reviewBinding: start?.reviewBinding,
            }, CHECKOUT_API_CONFIG);
          const state = res?.data ?? res;
          const saved: Array<{ id?: string; type?: string }> = state?.paymentTokens ?? [];
          // Prefer a card; other token types may not be chargeable on this rail.
          const chosen = saved.find((t) => t.type === 'card') ?? saved[0];
          if (chosen?.id) {
            setPaymentToken(chosen.id);
            setPhase('card-added');
            reportStep('payment_method');
            return true;
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[onramp] customer-state lookup failed, collecting a card instead', err);
        }
      }

      const collected = await new Promise<string>((resolve, reject) => {
        coordinator
          .collectPaymentMethod(
            {
              payment_method_types: ['card'],
              // Required by the SDK — 3thix's own client notes that omitting wallets fails.
              wallets: { applePay: 'never', googlePay: 'never' },
            },
            (request) => {
              const raw = request?.cryptoPaymentToken ?? request?.payment_token
                ?? request?.paymentToken ?? request?.id;
              const value = typeof raw === 'string' ? raw : raw?.id;
              if (!value) {
                reject(new Error('No payment method was added. Please try again.'));
                return;
              }
              resolve(value);
            },
          )
          .then((element) => {
            if (element && cardHostRef.current) {
              cardHostRef.current.replaceChildren(element);
            }
          })
          .catch(reject);
      });

      setPaymentToken(collected);
      if (cardHostRef.current) cardHostRef.current.replaceChildren();
      setPhase('card-added');
      reportStep('payment_method');
      return true;
    } catch (err: any) {
      setError(err?.message || 'Could not load the payment form.');
      setPhase('error');
      reportStep(undefined, 'payment_method_error');
      // eslint-disable-next-line no-console
      console.error('[onramp] collectPaymentMethod failed', err);
      return false;
    }
  }, [token, start, cryptoCustomerId, reportStep]);

  /** Tells the provider a review is outstanding, so a reload doesn't re-ask for it. */
  const reportReviewPending = useCallback(
    async (kind: 'kyc' | 'document', intentId: string) => {
      if (!token || !cryptoCustomerId || !authedIntent?.binding) return;
      try {
        await apiService.post(`/checkout/orders/${token}/onramp/review-pending`, {
          kind, authIntentId: intentId, cryptoCustomerId,
          reviewBinding: authedIntent.binding,
        }, CHECKOUT_API_CONFIG);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[onramp] could not record review state', err);
      }
    },
    [token, cryptoCustomerId, authedIntent],
  );

  /**
   * Collects the extra verification the provider asked for at session creation.
   *
   * Only tier l2 is a document check (photo ID + selfie). l0/l1 mean the basic identity data was
   * insufficient rather than absent, which the profile step already covers — re-launching the
   * camera for those is the "keeps making you verify with photo ID" loop their client warns about,
   * so those are reported back to the shopper instead.
   *
   * @returns true when something was submitted and a retry is worthwhile.
   */
  const resolveStepUp = useCallback(
    async (tier: string | undefined, intentId: string): Promise<boolean> => {
      const coordinator = coordinatorRef.current;
      if (tier !== 'l2' || !coordinator?.verifyDocuments) {
        setError('This purchase needs additional identity verification. '
          + 'Please check your details and try again.');
        setPhase('error');
        reportStep(undefined, 'kyc_submission_error');
        return false;
      }
      setError('');
      setPhase('verifying-document');
      try {
        const result: any = await coordinator.verifyDocuments();
        if (result?.result === 'abandoned') {
          setError('Identity verification wasn\'t completed. Please try again.');
          setPhase('error');
          reportStep(undefined, 'document_abandoned');
          return false;
        }
        // Recorded BEFORE the retry: the review is asynchronous, so if the shopper reloads while
        // it is still running this is what stops them being sent back through the camera.
        await reportReviewPending('document', intentId);
        return true;
      } catch (err: any) {
        setError(err?.message || 'Identity verification failed.');
        setPhase('error');
        reportStep(undefined, 'document_error');
        // eslint-disable-next-line no-console
        console.error('[onramp] verifyDocuments failed', err);
        return false;
      }
    },
    [reportReviewPending, reportStep],
  );

  /**
   * Slice 4b — mint the session and charge.
   *
   * performCheckout hands its callback an onrampSessionId and expects a checkout clientSecret
   * back. Only 3thix can mint that, so the callback round-trips through our backend; Stripe then
   * drives 3DS and settlement itself.
   *
   * The closing sync is best-effort ON PURPOSE. Our own transaction is marked paid by 3thix's
   * webhook and ThixReconciliationScheduler, never by this call — so a shopper closing the tab
   * mid-sync cannot strand the order.
   */
  const payNow = useCallback(async () => {
    const coordinator = coordinatorRef.current;
    if (!coordinator || !start || !paymentToken || !token) return false;
    setError('');
    setPhase('paying');
    try {
      // Must exist and must be the authenticated one — see the authedIntent note above.
      const intentId = authedIntent?.id ?? start.authIntentId;
      if (!intentId) {
        setError('Your verification session has expired. Please start the payment again.');
        setPhase('error');
        return false;
      }
      const created: any = await apiService.post(
        `/checkout/orders/${token}/onramp/session`, {
          authIntentId: intentId,
          cryptoCustomerId,
          paymentToken,
          // Required. Omitting country is what 3thix rejects with the bare zod message
          // "Invalid input: expected string, received undefined" — it names no field, so this
          // read as a data problem for a while when it was a missing-parameter problem.
          country: kycCountry,
          state: kycState || undefined,
          changePaymentMethod: false,
        }, CHECKOUT_API_CONFIG);
      let payload = created?.data ?? created;

      // The provider can answer "this amount needs a higher verification tier" instead of a
      // session. That is not a failure — the shopper can clear it — so collect what is asked for
      // and mint the session again. Only ONE retry: if it comes back still needing a step-up, the
      // review is asynchronous and the shopper has to wait rather than loop through the camera.
      if (payload?.stepUpRequired) {
        const cleared = await resolveStepUp(payload.tier, intentId);
        if (!cleared) return false;
        const retried: any = await apiService.post(
          `/checkout/orders/${token}/onramp/session`, {
            authIntentId: intentId,
            cryptoCustomerId,
            paymentToken,
            country: kycCountry,
            state: kycState || undefined,
            changePaymentMethod: false,
          }, CHECKOUT_API_CONFIG);
        payload = retried?.data ?? retried;
        if (payload?.stepUpRequired) {
          setError('Your identity documents are still being reviewed. '
            + 'Please keep this page open and try again in a few minutes.');
          setPhase('review-pending');
          return false;
        }
      }

      const sessionId: string = payload?.sessionId;
      if (!sessionId) throw new Error('Could not start the payment session.');
      setOnrampSessionId(sessionId);

      await coordinator.performCheckout(sessionId, async (sid: string) => {
        const res: any = await apiService.post(
          `/checkout/orders/${token}/onramp/session/${encodeURIComponent(sid)}/checkout`,
          // Residence goes on this leg too — 3thix answers "residence_unverified" without it.
          { authIntentId: intentId, country: kycCountry, state: kycState || undefined },
          CHECKOUT_API_CONFIG);
        const secret: string = (res?.data ?? res)?.clientSecret;
        if (!secret) throw new Error('Checkout could not be completed.');
        return secret;
      });

      void apiService
        .post(`/checkout/orders/${token}/onramp/session/${encodeURIComponent(sessionId)}/sync`, {}, CHECKOUT_API_CONFIG)
        .catch(() => undefined);

      setPhase('paid');
      reportStep('checkout');
      return true;
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Payment could not be completed.');
      setPhase('error');
      reportStep(undefined, 'session_error');
      // eslint-disable-next-line no-console
      console.error('[onramp] performCheckout failed', err);
      return false;
    }
  }, [start, token, paymentToken, cryptoCustomerId, kycCountry, kycState,
      authedIntent, resolveStepUp, reportStep]);

  return {
    phase, error, start, begin, authenticateLink,
    missing, beginProfile, submitProfile, loadKycRequirements, submitIdentifiers,
    cryptoCustomerId, otpHostRef, cardHostRef,
    collectCard, payNow, paymentToken, onrampSessionId,
    reportStep, coordinatorRef,
  };
}
